import { Message, AnimationType, AnimationConfig } from '../types';

/**
 * Creates a message object with default timestamp and id
 */
export function createMessage(
  type: Message['type'],
  content: string,
  options?: Partial<Omit<Message, 'type' | 'content' | 'timestamp'>> & {
    id?: string;
  }
): Message {
  // Extract id from options to handle it separately
  const { id, ...restOptions } = options ?? {};

  return {
    id: id || crypto.randomUUID(),
    type,
    content,
    timestamp: new Date(),
    ...restOptions,
  };
}

/**
 * Creates a user message
 */
export function createUserMessage(
  content: string,
  isChatMessage = false,
  options?: Partial<Message>
): Message {
  return createMessage('user', content, { isChatMessage, ...options });
}

/**
 * Creates an assistant message
 */
export function createAssistantMessage(
  content: string,
  options?: Partial<Message>
): Message {
  return createMessage('assistant', content, options);
}

/**
 * Creates an error message
 */
export function createErrorMessage(
  content: string,
  options?: Partial<Message>
): Message {
  return createMessage('assistant', content, { isError: true, ...options });
}

/**
 * Creates a thinking message
 */
export function createThinkingMessage(
  content: string,
  options?: Partial<Message>
): Message {
  return createMessage('assistant', content, { isThinking: true, ...options });
}

/**
 * Creates a message with animation configuration
 */
export function createAnimatedMessage(
  type: Message['type'],
  content: string,
  animationType: AnimationType,
  options?: {
    delay?: number;
    speed?: number;
    config?: AnimationConfig['config'];
    id?: string;
  } & Partial<Omit<Message, 'type' | 'content' | 'timestamp' | 'animation'>>
): Message {
  const animation: AnimationConfig = {
    type: animationType,
    delay: options?.delay,
    speed: options?.speed,
    config: options?.config,
  };

  const { delay, speed, config, ...messageOptions } = options ?? {};

  return createMessage(type, content, {
    ...messageOptions,
    animation,
  });
}

/**
 * Creates an assistant message with line-by-line animation
 */
export function createLineByLineMessage(
  content: string,
  delay = 250,
  options?: Partial<Message>
): Message {
  return createAnimatedMessage('assistant', content, 'line-by-line', {
    delay,
    ...options,
  });
}

/**
 * Creates an assistant message with typewriter animation
 */
export function createTypewriterMessage(
  content: string,
  charDelay = 30,
  options?: Partial<Message>
): Message {
  return createAnimatedMessage('assistant', content, 'typewriter', {
    delay: charDelay,
    config: { charDelay },
    ...options,
  });
}

/**
 * Creates an assistant message with progress bar animation
 */
export function createProgressMessage(
  content: string,
  progressSteps: Array<{ label: string; duration: number }>,
  options?: Partial<Message>
): Message {
  return createAnimatedMessage('assistant', content, 'progress', {
    config: { progressSteps },
    ...options,
  });
}
